import { PagedEntityList, EmployeeIdentityResponse, AdpImportFileDefinitionSelectResponse, PayrollListItem } from '@herbjoy/apex-api-models'
import { IQService } from 'angular'
import moment from 'moment'
import { EmployeeService, PayrollService } from '../../../core/services'
import { UtilityService } from '../../../core/services/utility.service'
import templateUrl from './payroll-processor-dashboard.component.html'
import { StateService } from 'angular-ui-router'

export const PayrollProcessorDashboardComponent = {
    templateUrl,
    require: {
        dashboardContext: "?^apexDashboardContent"
    },
    controllerAs: "vm",
    controller: class PayrollAdminDashboardController {
        loaded: boolean
        showOnlyMyOwnWork: boolean
        filters: { sort: string[] }
        showFilters: boolean
        unassignedPayrollsPage: PagedEntityList<any>
        workingPayrollsPage: PagedEntityList<any>
        processedPayrollsPage: PagedEntityList<any>
        dashboard: any
        dashboardContext: any
        employee: EmployeeIdentityResponse
        anyPayrolls: boolean
        adpConfigs: AdpImportFileDefinitionSelectResponse[]
        constructor(private $q: IQService, private $state: StateService, private apexApiUrl: string,
            private employeeService: EmployeeService,
            private payrollService: PayrollService,
            private utilityService: UtilityService) {
            'ngInject'

            this.loaded = false;
            // The Working On list is filtered to only display the employee's own work.
            this.showOnlyMyOwnWork = true
            // Any filters that should be applied the the payroll list.
            this.filters = {
                sort: ["LockedDate", "EndDate", "Owner"]
            }
            this.showFilters = false
            // Payrolls that should appear on the Submission Page (newly submitted and unassigned).
            this.unassignedPayrollsPage = {
                PageIndex: 0,
                PageSize: 10,
                TotalEntityCount: 0
            }
            // Payrolls that have an HJ Payroll Specialist assigned.
            this.workingPayrollsPage = {
                PageIndex: 0,
                PageSize: 5, // These rows are taller, so display fewer rows per page.
                TotalEntityCount: 0
            }
            // The Working On list is filtered to only display the employee's own work.
            this.processedPayrollsPage = {
                PageIndex: 0,
                PageSize: 10,
                TotalEntityCount: 0
            }
        }

        $onInit() {
            this.dashboard = this.dashboardContext ? this.dashboardContext.dashboard : null;
            // Get employee details in case they assign a payroll to themselves.
            this.employeeService.retrieveIdentity()
                .then(employee => {
                    this.employee = employee

                    return this.$q.all([
                        this.loadUnassignedPage(),
                        this.loadWorkingOnPage(),
                        this.loadProcessedPage()
                    ])
                })
                .then(() => {
                    this.anyPayrolls = this.unassignedPayrollsPage.TotalEntityCount > 0
                        || this.workingPayrollsPage.TotalEntityCount > 0
                        || this.processedPayrollsPage.TotalEntityCount > 0

                    if (this.dashboardContext) {
                        // Activate the dashboard parent for this component based on data being loaded.
                        this.dashboardContext.activateDashboard({
                            visible: true,
                            count: this.unassignedPayrollsPage.TotalEntityCount
                                + this.workingPayrollsPage.TotalEntityCount
                        })
                    }
                })
                .catch(ex => {
                    console.error(ex)
                })
                .then(() => this.loaded = true)
        }

        onChangeFilters() {
            this.unassignedPayrollsPage.PageIndex = 0
            this.workingPayrollsPage.PageIndex = 0
            this.processedPayrollsPage.PageIndex = 0

            const reloadData = this.$q.all([
                this.loadUnassignedPage(),
                this.loadWorkingOnPage(),
                this.loadProcessedPage()
            ])

            this.utilityService.showLoadingUntilResolved(reloadData)
        }

        onChangeShowOwnWork() {
            this.workingPayrollsPage.PageIndex = 0
            this.utilityService.showLoadingUntilResolved(this.loadWorkingOnPage())
        }

        onChangeUnassignedPayrollsPage({ pageIndex }) {
            this.unassignedPayrollsPage.PageIndex = pageIndex
            this.utilityService.showLoadingUntilResolved(this.loadUnassignedPage())
        }

        onChangeWorkingOnPayrollsPage({ pageIndex }) {
            this.workingPayrollsPage.PageIndex = pageIndex
            this.utilityService.showLoadingUntilResolved(this.loadWorkingOnPage())
        }

        onChangeProcessedPayrollsPage({ pageIndex }) {
            this.processedPayrollsPage.PageIndex = pageIndex
            this.utilityService.showLoadingUntilResolved(this.loadProcessedPage())
        }

        loadUnassignedPage() {
            setValidPageIndex(this.unassignedPayrollsPage);
            return this.payrollService.retrievePayrollsPageForProcessors({
                ...createPayrollFilterArguments(this.filters),
                assigned: false,
                complete: false
            }, this.unassignedPayrollsPage.PageIndex, this.unassignedPayrollsPage.PageSize)
                .then(page => {
                    this.unassignedPayrollsPage = page
                })
        }

        loadWorkingOnPage() {
            setValidPageIndex(this.workingPayrollsPage);
            return this.payrollService.retrievePayrollsPageForProcessors({
                ...createPayrollFilterArguments(this.filters),
                assigned: true,
                complete: false,
                processor: this.showOnlyMyOwnWork ? this.employee.EmployeeID : null
            }, this.workingPayrollsPage.PageIndex, this.workingPayrollsPage.PageSize)
                .then(page => {
                    this.workingPayrollsPage = page
                })
        }

        loadProcessedPage() {
            setValidPageIndex(this.processedPayrollsPage);
            return this.payrollService.retrievePayrollsPageForProcessors({
                ...createPayrollFilterArguments(this.filters),
                Sort: ["CompletedDateDesc", "EndDate", "Owner"],
                complete: true
            }, this.processedPayrollsPage.PageIndex, this.processedPayrollsPage.PageSize)
                .then(page => {
                    this.processedPayrollsPage = page
                })
        }

        assignPayroll({ payroll }) {
            const saving = this.payrollService.assignPayrollAdmin(payroll.PayrollID, this.employee.EmployeeID)

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(saving, "Assigning failed")
            )

            // The unassigned page and working on page data will have changed.
            const reloading = saving.then(() => {
                // Update entity counts used to assess the next page we should load.
                this.unassignedPayrollsPage.TotalEntityCount--;
                this.workingPayrollsPage.TotalEntityCount++;
            })
                .then(() => this.$q.all([this.loadUnassignedPage(), this.loadWorkingOnPage()]))
            this.utilityService.showLoadingUntilResolved(reloading)

            return saving
        }

        unassignPayroll({ payroll }) {
            const saving = this.payrollService.unassignPayrollAdmin(payroll.PayrollID, this.employee.EmployeeID)

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(saving, "Unassigning failed")
            )

            // The unassigned page and working on page data will have changed.
            const reloading = saving.then(() => {
                // Update entity counts used to assess the next page we should load.
                this.unassignedPayrollsPage.TotalEntityCount++;
                this.workingPayrollsPage.TotalEntityCount--;
            })
                .then(() => this.$q.all([this.loadUnassignedPage(), this.loadWorkingOnPage()]))
            this.utilityService.showLoadingUntilResolved(reloading)

            return reloading
        }

        /**
         * Update whether the given payroll is pending.
         */
        changePayrollPending({ payroll, pending }) {
            this.utilityService.showLoading();
            return this.payrollService.savePayrollPending(payroll.PayrollID, pending)
                .then(function () {
                    // A full reload isn't necessary beyond setting the pending value.
                    payroll.Pending = pending;
                })
                .catch(ex => alert("Setting pending failed:" + this.utilityService.getExceptionMessage(ex)))
                .then(() => this.utilityService.hideLoading());
        }

        /**
         * Update whether the payroll is completed.
         */
        changePayrollCompleted({ payroll, completed }) {
            const saving = this.payrollService.savePayrollCompleted(payroll.PayrollID, completed)

            this.utilityService.showLoadingUntilResolved(
                this.utilityService.alertOnError(saving, "Setting completed failed")
            )

            // The working on and processed pages will change as a result of this.
            const reloading = saving.then(() => {
                // Update entity counts used to assess the next page we should load.
                this.unassignedPayrollsPage.TotalEntityCount += completed ? 1 : -1;
                this.workingPayrollsPage.TotalEntityCount += completed ? -1 : 1;
            })
                .then(() => this.$q.all([
                    this.loadWorkingOnPage(),
                    this.loadProcessedPage()
                ]))

            this.utilityService.showLoadingUntilResolved(reloading)

            return reloading
        }

        downloadPayrollImportFile({ payroll }: { payroll: PayrollListItem }) {
            // Navigate to the exporter state.
            return this.$state.go(".adpImportFileExporter", { payrollID: payroll.PayrollID });
        }

        downloadPayrollSummary({ payroll }) {
            const urlStrPayroll = `${this.apexApiUrl}/payrolls/adp-payroll-summary?`
                + `icdCorporationID=${payroll.IcdCorporationID}`
                + `&startDate=${moment(payroll.StartDate).format("YYYY-MM-DD")}`
                + `&endDate=${moment(payroll.EndDate).format("YYYY-MM-DD")}`
            window.location.href = urlStrPayroll;
        }

        unlockPayroll({ payroll }) {
            const payrollID = payroll.PayrollID;
            if (confirm('This action will unlock this payroll and the owner will be able to modify it.')) {
                const unlocking = this.payrollService.unlockPayroll(payrollID);
                this.utilityService.showLoadingUntilResolved(
                    this.utilityService.alertOnError(unlocking, "Unlocking failed")
                )
                // The unassigned page and working on page data will have changed.
                const reloading = unlocking.then(() => {
                    // Update entity counts used to assess the next page we should load.
                    this.unassignedPayrollsPage.TotalEntityCount--;
                })
                    .then(() => this.$q.all([this.loadUnassignedPage()]))
                this.utilityService.showLoadingUntilResolved(reloading)

                return reloading
            }
        }
    }
}

function createPayrollFilterArguments(viewFilters) {
    return {
        Owner: viewFilters.owner,
        ClientName: viewFilters.clientName,
        Start: viewFilters.checkDate,
        End: viewFilters.checkDate,
        Sort: viewFilters.sort
    }
}

function setValidPageIndex(page) {
    const maxIndex = maxPageIndex(page);
    if (page.PageIndex > maxIndex) {
        page.PageIndex = maxIndex;
    }
}

/**
 * Return the max size for a page structure.
 * @param {any} page The paged entity list representing page size and entity count.
 */
function maxPageIndex({ PageSize: pageSize, TotalEntityCount: totalEntityCount }: PagedEntityList<any>) {
    return Math.max(0, pageSize <= 0 ? 0
        : Math.floor(totalEntityCount / pageSize) + (totalEntityCount % pageSize > 0 ? 0 : -1))
}
